import React, { useMemo, useReducer, useEffect, useCallback, useRef } from "react";
import { useSelector } from "react-redux";
import { getCookie } from "../utils/cookiesHelper";
import stompChatService from "../../../../services/StompChatService";
import ChatServices from "../services/ChatServices";

import ChatContext from "./ChatContext";
import chatReducer, { CHAT_INITIAL_STATE } from "../reducers/chatReducer";
import CHAT_ACTIONS_TYPES from "../reducers/actionTypes/chatActionTypes";

// Lazy-load notification audio to avoid loading on login page
let notificationAudio = null;
const playNotificationSound = () => {
  try {
    if (!notificationAudio) {
      // eslint-disable-next-line global-require
      const audioSrc = require("../../../../assets/notification.wav");
      notificationAudio = new Audio(audioSrc);
    }
    notificationAudio.currentTime = 0;
    notificationAudio.play().catch((err) => {
      console.warn("Failed to play notification sound:", err);
    });
  } catch (err) {
    console.warn("Failed to load notification audio:", err);
  }
};

const ChatContextProvider = ({ children }) => {
  const [state, dispatch] = useReducer(chatReducer, CHAT_INITIAL_STATE);
  const orgUserToken = getCookie("X-User-Token");
  // Get orgId and userId from Redux store
  const orgId = useSelector((reduxState) =>
    reduxState.getIn(["users", "user", "info", "organization_id"])
  );
  const userId = useSelector((reduxState) =>
    reduxState.getIn(["users", "user", "info", "id"])
  );

  // Socket connection effect
  useEffect(() => {
    if (orgUserToken && userId) {
      const onConnected = (frame) => {
        // Socket connection is now managed through ChatContext state
        dispatch({
          type: "SOCKET_CONNECTED",
          payload: { connected: true }
        });
      };

      const onMessage = (payload) => {
        // Skip bot messages completely (more specific check)
        // Based on senderType: "USER" = Customer, "AGENT" = Agent, "bot" = Bot
        const isErrorMessage = payload.content?.toLowerCase().includes("sorry, i encountered an error");
        if (isErrorMessage) return;

        // Play notification sound for incoming customer messages
        const isFromCustomer = payload.senderType === "USER" || (payload.senderId && String(payload.senderId) !== String(userId));
        const isNewMessage = payload.type === "new_message" || (payload.chatId && payload.content && !payload.type);
        if (isFromCustomer && isNewMessage) {
          playNotificationSound();
          if (document.hidden) {
            document.title = "New Message...";
          }
        }

        // Handle different message types (only non-bot messages)
        if (payload.type === "new_message") {
          dispatch({
            type: "ADD_NEW_MESSAGE_TO_ROOM",
            payload: { newMessage: payload, currentChatId: payload.chatId, force: true },
          });

          // Update rooms latestMessage
          dispatch({
            type: "UPDATE_LATEST_MESSAGE_AND_SORT",
            payload: {
              roomUpdated: {
                _id: payload.chatId || payload._id,
                latestMessage: payload,
                updatedAt: payload.createdAt || new Date().toISOString()
              }
            }
          });
        } else if (payload.type === "update_chat_latest_message") {
          dispatch({
            type: "UPDATE_LATEST_MESSAGE_AND_SORT",
            payload: { roomUpdated: payload },
          });
        } else if (payload.type === "chat_room_updated") {
          dispatch({
            type: "UPDATE_ROOM",
            payload: { roomUpdated: payload },
          });
        } else if (payload.chatId && payload.content) {
          // Regular message - add to room and update latest message

          dispatch({
            type: "ADD_NEW_MESSAGE_TO_ROOM",
            payload: { newMessage: payload, currentChatId: payload.chatId },
          });

          // Also update the latest message in the room
          dispatch({
            type: "UPDATE_LATEST_MESSAGE_AND_SORT",
            payload: {
              roomUpdated: {
                _id: payload.chatId,
                latestMessage: payload,
                updatedAt: payload.createdAt || new Date().toISOString()
              }
            },
          });
        }
      };

      const onError = (error) => {
        console.error("STOMP WebSocket connection error:", error);
      };

      stompChatService.connect(orgUserToken, onConnected, onMessage, onError, orgId, userId);
    }

    return () => {
      stompChatService.disconnect();
    };
  }, [orgUserToken, orgId, userId]);

  // ---- Group chat helpers ----

  const selectedGroupRoomRef = useRef(null);

  const fetchGroupRooms = useCallback(async () => {
    dispatch({ type: CHAT_ACTIONS_TYPES.SET_LOADING_GROUP_ROOMS, payload: { loadingGroupRooms: true } });
    try {
      const rooms = await ChatServices.getGroupRooms({ jwtToken: orgUserToken });
      dispatch({ type: CHAT_ACTIONS_TYPES.SET_GROUP_ROOMS, payload: { groupRooms: rooms } });
    } catch (e) {
      console.error("Failed to load group rooms:", e);
    } finally {
      dispatch({ type: CHAT_ACTIONS_TYPES.SET_LOADING_GROUP_ROOMS, payload: { loadingGroupRooms: false } });
    }
  }, [orgUserToken]);

  const fetchGroupMessages = useCallback(async ({ groupId, page = 0, mode = "replace" }) => {
    dispatch({ type: CHAT_ACTIONS_TYPES.SET_LOADING_GROUP_CHAT, payload: { loadingGroupChat: true } });
    try {
      const result = await ChatServices.getGroupMessages({ jwtToken: orgUserToken, groupId, page });
      const actionType = mode === "appendOlder"
        ? CHAT_ACTIONS_TYPES.APPEND_OLDER_GROUP_MESSAGES
        : CHAT_ACTIONS_TYPES.SET_GROUP_MESSAGES;
      dispatch({ type: actionType, payload: { groupMessages: result.content } });
      dispatch({ type: CHAT_ACTIONS_TYPES.SET_GROUP_HAS_MORE, payload: { groupHasMore: !result.last } });
      dispatch({ type: CHAT_ACTIONS_TYPES.SET_GROUP_PAGE, payload: { groupPage: page } });
    } catch (e) {
      console.error("Failed to load group messages:", e);
    } finally {
      dispatch({ type: CHAT_ACTIONS_TYPES.SET_LOADING_GROUP_CHAT, payload: { loadingGroupChat: false } });
    }
  }, [orgUserToken]);

  const checkGroupMembership = useCallback(async (groupId) => {
    try {
      const details = await ChatServices.getGroupDetails({ jwtToken: orgUserToken, groupId });
      const members = details?.users || [];
      const joined = members.some(
        (m) => m.chatUser?.id === userId || m.externalUserId === userId || m.chatUser?.id === orgId
      );
      dispatch({ type: CHAT_ACTIONS_TYPES.SET_GROUP_JOINED, payload: { isGroupJoined: joined } });
      if (joined) {
        await fetchGroupMessages({ groupId, page: 0, mode: "replace" });
      }
    } catch (e) {
      console.error("Failed to check group membership:", e);
    }
  }, [orgUserToken, userId, orgId, fetchGroupMessages]);

  const selectGroupRoom = useCallback(async (room) => {
    selectedGroupRoomRef.current = room;
    dispatch({ type: CHAT_ACTIONS_TYPES.SET_SELECTED_GROUP_ROOM, payload: { selectedGroupRoom: room } });
    await checkGroupMembership(room.id || room._id);
  }, [checkGroupMembership]);

  const joinGroup = useCallback(async (groupId) => {
    try {
      await ChatServices.joinGroup({ jwtToken: orgUserToken, groupId });
      dispatch({ type: CHAT_ACTIONS_TYPES.SET_GROUP_JOINED, payload: { isGroupJoined: true } });
      await fetchGroupMessages({ groupId, page: 0, mode: "replace" });
    } catch (e) {
      // 400 = already member — treat as joined
      if (e?.response?.status === 400) {
        dispatch({ type: CHAT_ACTIONS_TYPES.SET_GROUP_JOINED, payload: { isGroupJoined: true } });
        await fetchGroupMessages({ groupId, page: 0, mode: "replace" });
      } else {
        console.error("Failed to join group:", e);
      }
    }
  }, [orgUserToken, fetchGroupMessages]);

  const sendGroupMessage = useCallback((content) => {
    const room = selectedGroupRoomRef.current;
    if (!room || !content?.trim()) return;
    const groupId = room.id || room._id;
    const tempMessage = {
      _id: `temp_${Date.now()}`,
      content,
      groupId,
      senderId: userId,
      senderName: "You",
      sender: { id: userId, externalUserId: userId, name: "You" },
      createdAt: new Date().toISOString(),
    };
    dispatch({ type: CHAT_ACTIONS_TYPES.ADD_NEW_GROUP_MESSAGE, payload: { newMessage: tempMessage } });
    stompChatService.sendGroupMessage(orgUserToken, { content, groupId, senderId: userId });
  }, [orgUserToken, userId]);

  // Group WebSocket subscription — re-subscribe whenever selectedGroupRoom changes
  const { selectedGroupRoom } = state;
  useEffect(() => {
    if (!selectedGroupRoom || !userId) return;
    const groupId = selectedGroupRoom.id || selectedGroupRoom._id;

    const handleGroupMessage = (payload) => {
      dispatch({ type: CHAT_ACTIONS_TYPES.ADD_NEW_GROUP_MESSAGE, payload: { newMessage: payload } });
    };

    stompChatService.subscribeGroupMessages(handleGroupMessage, userId, groupId);

    return () => {
      stompChatService.unsubscribeGroupMessages();
    };
  }, [selectedGroupRoom, userId]);

  const providerState = useMemo(
    () => ({
      state,
      dispatch,
      // Group helpers exposed via context
      fetchGroupRooms,
      selectGroupRoom,
      joinGroup,
      sendGroupMessage,
      fetchGroupMessages,
    }),
    [state, fetchGroupRooms, selectGroupRoom, joinGroup, sendGroupMessage, fetchGroupMessages]
  );

  return (
    <ChatContext.Provider value={providerState}>
      {children}
    </ChatContext.Provider>
  );
};

export default ChatContextProvider;
